/* eslint-disable */
/* ============================================================
   ascii-cta.jsx — URL-driven ASCII portrait generator
   ------------------------------------------------------------
   Flow:
     1. User pastes a social URL or @handle into the text input.
     2. On submit: POST /api/ascii-log (fire-and-forget), then
        GET /api/avatar?q=<input> which proxies Unavatar.io.
     3. The returned image blob is loaded into an <img> element
        and passed through imageToAscii() (same in-browser
        converter as before — 80 cols, 12-level ramp).
     4. Result shown with Download .PNG, Copy .TXT, and Share
        buttons.

   No file upload. No drag-and-drop. No FormData.
   ============================================================ */

import React, {
  useState as _useStateCTA,
  useRef as _useRefCTA,
  useEffect as _useEffectCTA,
  useCallback as _useCBcta,
} from 'react';
import ReactDOM from 'react-dom';
import { AsciiBadge } from './components.jsx';

/* ── in-browser ASCII converter (unchanged from original) ── */
const ASCII_RAMP = " .,:;-=+*#%@"; // 12 levels, light → dark

function imageToAscii(img, cols = 80) {
  const aspect = img.height / img.width;
  const rows = Math.max(20, Math.floor(cols * aspect * 0.5));
  const c = document.createElement("canvas");
  c.width = cols;
  c.height = rows;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, cols, rows);
  const { data } = ctx.getImageData(0, 0, cols, rows);
  let out = "";
  for (let y = 0; y < rows; y++) {
    let line = "";
    for (let x = 0; x < cols; x++) {
      const i = (y * cols + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const idx = Math.floor(lum * (ASCII_RAMP.length - 1));
      line += ASCII_RAMP[ASCII_RAMP.length - 1 - idx];
    }
    out += line + "\n";
  }
  return out;
}

/* ── render ASCII text to a 1200×1200 canvas PNG ── */
function asciiToCanvas(asciiStr) {
  const lines = asciiStr.split("\n").filter((l) => l.length > 0);
  const SIZE = 1200;
  const FONT_SIZE = 13;
  const LINE_H = 14;
  const FONT = `${FONT_SIZE}px 'JetBrains Mono', monospace`;

  const c = document.createElement("canvas");
  c.width = SIZE;
  c.height = SIZE;
  const ctx = c.getContext("2d");

  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, SIZE, SIZE);

  ctx.font = FONT;
  ctx.fillStyle = "#e8e6dc";
  ctx.textBaseline = "top";

  const charW = ctx.measureText("M").width;
  const totalW = (lines[0]?.length ?? 80) * charW;
  const totalH = lines.length * LINE_H;
  const offsetX = Math.max(0, (SIZE - totalW) / 2);
  const offsetY = Math.max(0, (SIZE - totalH) / 2);

  lines.forEach((line, i) => {
    ctx.fillText(line, offsetX, offsetY + i * LINE_H);
  });

  return c;
}

/* ── main tool component ── */
function AsciiPicCTA() {
  const [ascii, setAscii] = _useStateCTA(null);
  const [busy, setBusy] = _useStateCTA(false);
  const [error, setError] = _useStateCTA(null);
  const [copied, setCopied] = _useStateCTA(false);
  const inputRef = _useRefCTA(null);
  const queryRef = _useRefCTA("");

  const runGenerate = _useCBcta(async (raw) => {
    const q = raw.trim();
    if (!q) return;
    queryRef.current = q;

    setBusy(true);
    setAscii(null);
    setError(null);
    setCopied(false);

    // Fire-and-forget log
    fetch("/api/ascii-log", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ input: q }),
    }).catch(() => {});

    try {
      const res = await fetch(`/api/avatar?q=${encodeURIComponent(q)}`);
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error === "upstream_error"
          ? `no avatar found (${res.status}) — try a different URL or handle`
          : json.error ?? `http ${res.status}`
        );
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const art = imageToAscii(img, 80);
        URL.revokeObjectURL(objectUrl);
        setAscii(art);
        setBusy(false);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setError("could not decode image from that URL");
        setBusy(false);
      };
      img.src = objectUrl;
    } catch (err) {
      setError(err.message ?? "unknown error");
      setBusy(false);
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    runGenerate(inputRef.current?.value ?? "");
  };

  const downloadPng = () => {
    if (!ascii) return;
    const canvas = asciiToCanvas(ascii);
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ascii-portrait.png";
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const copyTxt = () => {
    if (!ascii) return;
    navigator.clipboard.writeText(ascii).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareTweet = () => {
    const text = encodeURIComponent(
      "converted my profile pic to ASCII at kevinz.ai — try it"
    );
    const url = encodeURIComponent("https://kevinz.ai/#ascii-tool");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank", "noopener");
  };

  const reset = () => {
    setAscii(null);
    setError(null);
    setBusy(false);
    setCopied(false);
    if (inputRef.current) inputRef.current.value = "";
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <section id="ascii-tool" data-section="ascii-cta" className="section-pad">
      <div className="container">
        <div className="box-head">
          <AsciiBadge glyph="bracket" count={3} />
          <span>// 09 — ascii portrait generator</span>
          <span className="rule" />
        </div>

        <div className="ascii-cta-wrap">
          {/* LEFT: pitch + input */}
          <div className="ascii-cta-left">
            <h2 className="h-section">
              your ASCII face,<br />
              <span className="amber">in 80 characters.</span>
            </h2>
            <p className="lede full mt-16" style={{ maxWidth: "44ch" }}>
              Paste a social profile URL or @handle. We fetch the avatar,
              run it through a 12-level ASCII ramp, and hand you the result.
              No upload. No account.
            </p>

            <ul style={{
              listStyle: "none", padding: 0, margin: "20px 0 0",
              fontFamily: "var(--mono)", fontSize: 12,
              color: "var(--fg-3)", lineHeight: 1.9
            }}>
              <li><span className="amber">▸</span> x · github · linkedin · instagram · youtube</li>
              <li><span className="amber">▸</span> 80×40 char render · 12-level ramp</li>
              <li><span className="amber">▸</span> download .png · copy .txt · share to X</li>
              <li><span className="amber">▸</span> we don't store the image — only the URL you submit</li>
            </ul>

            <form onSubmit={onSubmit} className="ascii-url-form mt-32">
              <div className="ascii-url-row">
                <input
                  ref={inputRef}
                  className="news-input ascii-url-input"
                  placeholder="https://github.com/KevinZai or @handle"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  disabled={busy}
                />
                <button
                  type="submit"
                  className="btn btn-primary ascii-url-btn"
                  disabled={busy}
                >
                  {busy ? "↻ fetching…" : "generate"}
                </button>
              </div>
              <div className="dim mt-8" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                @handle defaults to x · linkedin often returns generic icon if profile is private
              </div>
            </form>

            {error && (
              <div className="ascii-error mt-16">
                <span className="amber">!</span> {error}
                <button className="btn" style={{ marginLeft: 12, fontSize: 10 }} onClick={reset}>
                  try again
                </button>
              </div>
            )}
          </div>

          {/* RIGHT: terminal window + output */}
          <div className="ascii-cta-right">
            <div className="win">
              <div className="win-title">
                <div className="lights">
                  <span className="r" /><span className="a" /><span className="g" />
                </div>
                <span>~/ascii-portrait · {ascii ? "ready" : busy ? "converting" : "waiting"}</span>
                <span className="meta">{ascii ? "80 cols · 12 levels" : "—"}</span>
              </div>

              <div className="ascii-cta-drop" style={{ cursor: "default" }}>
                {!ascii && !busy && !error && (
                  <div className="ascii-cta-empty">
                    <pre className="dim" style={{ margin: 0, fontSize: 11, lineHeight: 1.3 }}>{`
   ┌────────────────────────┐
   │                        │
   │   paste a URL above    │
   │   and hit generate     │
   │                        │
   │  x · github · linkedin │
   │  instagram · youtube   │
   │                        │
   └────────────────────────┘`}</pre>
                  </div>
                )}

                {busy && (
                  <div className="amber" style={{
                    fontFamily: "var(--mono)", fontSize: 12,
                    letterSpacing: "0.18em", textTransform: "uppercase"
                  }}>
                    ↻ fetching avatar · converting · 80 cols · 12 levels<span className="cursor" />
                  </div>
                )}

                {ascii && (
                  <pre className="ascii-cta-preview">{ascii}</pre>
                )}
              </div>

              <div className="win-foot">
                <span>{ascii ? "client-side render" : busy ? "working…" : "ready"}</span>
                <span>{ascii ? "save → set manually on your platform" : "paste URL to begin"}</span>
              </div>
            </div>

            {ascii && (
              <div className="row gap-12 mt-12" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button className="btn" onClick={reset}>× clear</button>
                <button className="btn" onClick={copyTxt}>
                  {copied ? "✓ copied" : "copy .txt"}
                </button>
                <button className="btn" onClick={downloadPng}>download .png</button>
                <button className="btn btn-primary" onClick={shareTweet}>
                  share → x
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .ascii-cta-wrap{
          display:grid; grid-template-columns:1fr 1.2fr; gap:32px;
          align-items:start;
        }
        @media(max-width:980px){ .ascii-cta-wrap{ grid-template-columns:1fr; } }

        .ascii-url-form{ width:100%; }
        .ascii-url-row{
          display:flex; gap:8px; align-items:stretch;
        }
        .ascii-url-input{
          flex:1; min-width:0;
        }
        .ascii-url-btn{
          white-space:nowrap; flex-shrink:0;
        }
        @media(max-width:600px){
          .ascii-url-row{ flex-direction:column; }
          .ascii-url-btn{ width:100%; justify-content:center; }
        }

        .ascii-error{
          font-family:var(--mono); font-size:12px;
          color:var(--red,#d56565); letter-spacing:0.04em;
        }

        .ascii-cta-drop{
          min-height:360px;
          padding:18px;
          background:#050606;
          display:flex; align-items:center; justify-content:center;
          transition:background 140ms ease;
          overflow:hidden;
        }
        .ascii-cta-empty{ text-align:center; }
        .ascii-cta-preview{
          font-family:var(--mono);
          font-size:6px;
          line-height:1;
          letter-spacing:0;
          color:var(--amber);
          text-shadow:0 0 6px color-mix(in oklch, var(--amber) 30%, transparent);
          white-space:pre;
          margin:0;
          user-select:all;
        }
        @media(max-width:600px){ .ascii-cta-preview{ font-size:5px; } }
      `}</style>
    </section>
  );
}

/* ============================================================
   AsciiCtaTeaser — compact homepage card → opens modal
   Deep-links: #ascii-tool, #ascii
   ============================================================ */
function AsciiCtaTeaser() {
  const [open, setOpen] = _useStateCTA(false);

  _useEffectCTA(() => {
    const isAsciiHash = () =>
      ["#ascii-tool", "#ascii"].includes(window.location.hash);
    if (isAsciiHash()) setOpen(true);
    const onHash = () => setOpen(isAsciiHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const handleClose = () => {
    setOpen(false);
    if (window.location.hash)
      history.replaceState(null, "", window.location.pathname);
  };

  _useEffectCTA(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section id="ascii-cta" data-section="ascii-cta" className="section-pad">
      <div className="container">
        <div className="box-head">
          <AsciiBadge glyph="bracket" count={3} />
          <span>// 09 — ascii portrait generator</span>
          <span className="rule" />
        </div>

        <div style={{
          marginTop: 20,
          border: "1px solid var(--line-strong)",
          background: "var(--bg-1)",
          padding: "28px 32px",
          display: "grid",
          gridTemplateColumns: "1.4fr auto",
          gap: 24,
          alignItems: "center"
        }} className="ascii-teaser">
          <div>
            <h2 className="h-section" style={{ marginBottom: 8 }}>
              your ASCII face,<br />
              <span className="dim2" style={{ fontWeight: 600 }}>in 80 characters.</span>
            </h2>
            <p style={{
              fontSize: 14, color: "var(--fg-3)",
              lineHeight: 1.55, maxWidth: "60ch", marginTop: 14
            }}>
              Paste your social URL or @handle. We fetch the avatar, run it
              through a 12-level ASCII ramp, and give you a portrait you can
              paste into your terminal banner, README, or X header. No upload.
              No account. Free.
            </p>
            <div style={{
              marginTop: 16, fontSize: 11,
              color: "var(--fg-4)", letterSpacing: "0.16em", textTransform: "uppercase"
            }}>
              ▸ x · github · linkedin · instagram · youtube
            </div>
          </div>

          <div style={{
            display: "flex", flexDirection: "column",
            gap: 10, alignItems: "stretch", minWidth: 220
          }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setOpen(true)}
              style={{ justifyContent: "center", whiteSpace: "nowrap" }}
            >
              ▸ open the tool
            </button>
            <a
              href="?view=ascii"
              className="btn btn-ghost"
              style={{ justifyContent: "center", whiteSpace: "nowrap", fontSize: 11 }}
            >
              ↗ standalone page
            </a>
          </div>
        </div>
      </div>

      {/* MODAL — portal to document.body to escape any ancestor stacking context */}
      {open && ReactDOM.createPortal && ReactDOM.createPortal(
        <div
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-label="ASCII portrait generator"
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.92)",
            backdropFilter: "blur(16px) saturate(0.8)",
            WebkitBackdropFilter: "blur(16px) saturate(0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "24px",
            overflowY: "auto",
            isolation: "isolate"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1180px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
              border: "1px solid var(--amber)",
              background: "var(--bg)"
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label="close"
              style={{
                position: "sticky", top: 8, marginLeft: "auto", marginRight: 8,
                display: "block", float: "right",
                fontSize: 11, color: "var(--amber)", letterSpacing: "0.18em",
                textTransform: "uppercase", cursor: "pointer",
                background: "var(--bg-1)", border: "1px solid var(--line)",
                padding: "6px 10px", zIndex: 10
              }}
            >× close · esc</button>
            <AsciiPicCTA />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
Object.assign(window, {
  AsciiPicCTA,
  AsciiCtaTeaser
});

export {
  AsciiPicCTA,
  AsciiCtaTeaser
};
